import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { EmployeeService } from '@/services/EmployeeService';
import type {
  EmployeeResponse,
  EmployeeRequest,
  WorkScheduleResponse,
  WorkScheduleRequest,
  EmployeeUnavailabilityResponse,
  EmployeeUnavailabilityRequest,
  EmployeeQualificationResponse,
  AbsenceForCalendarResponse
} from '@/types/employee';
import {
  EmployeeListItem,
  WorkScheduleListItem,
  EmployeeUnavailabilityListItem,
  EmployeeQualificationListItem
} from '@/types/employee';

export const useEmployeeStore = defineStore('employee', () => {
  // State
  const employees = ref<EmployeeListItem[]>([]);
  const currentEmployee = ref<EmployeeListItem | null>(null);
  const employeeQualifications = ref<EmployeeQualificationListItem[]>([]);
  const employeeSchedules = ref<WorkScheduleListItem[]>([]);
  const employeeUnavailabilities = ref<EmployeeUnavailabilityListItem[]>([]);
  const absencesForCalendar = ref<AbsenceForCalendarResponse[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  // Getters
  const activeEmployees = computed(() =>
    employees.value.filter((emp) => emp.isActive)
  );

  const employeesWithExpiringQualifications = computed(() =>
    employeeQualifications.value.filter(
      (qual) => qual.expiryStatus === 'expiring-soon'
    )
  );

  const employeesWithExpiredQualifications = computed(() =>
    employeeQualifications.value.filter((qual) => qual.expiryStatus === 'expired')
  );

  // Actions - Employee CRUD
  const loadEmployees = async (): Promise<void> => {
    loading.value = true;
    error.value = null;
    try {
      const response = await EmployeeService.getEmployees();
      employees.value = response.map((emp) => new EmployeeListItem(emp));
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load employees';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const loadEmployee = async (employeeId: number): Promise<void> => {
    loading.value = true;
    error.value = null;
    try {
      const response = await EmployeeService.getEmployeeById(employeeId);
      currentEmployee.value = new EmployeeListItem(response);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load employee';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const createEmployee = async (request: EmployeeRequest): Promise<EmployeeListItem> => {
    loading.value = true;
    error.value = null;
    try {
      const response = await EmployeeService.createEmployee(request);
      const newEmployee = new EmployeeListItem(response);
      employees.value.push(newEmployee);
      return newEmployee;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create employee';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const updateEmployee = async (
    employeeId: number,
    request: EmployeeRequest
  ): Promise<void> => {
    loading.value = true;
    error.value = null;
    try {
      const response = await EmployeeService.updateEmployee(employeeId, request);
      const updatedEmployee = new EmployeeListItem(response);

      const index = employees.value.findIndex((emp) => emp.employeeId === employeeId);
      if (index !== -1) {
        employees.value[index] = updatedEmployee;
      }

      if (currentEmployee.value?.employeeId === employeeId) {
        currentEmployee.value = updatedEmployee;
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update employee';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const deleteEmployee = async (employeeId: number): Promise<void> => {
    loading.value = true;
    error.value = null;
    try {
      await EmployeeService.deleteEmployee(employeeId);
      employees.value = employees.value.filter((emp) => emp.employeeId !== employeeId);

      if (currentEmployee.value?.employeeId === employeeId) {
        currentEmployee.value = null;
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete employee';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  // Actions - Qualifications
  const loadEmployeeQualifications = async (employeeId: number): Promise<void> => {
    loading.value = true;
    error.value = null;
    try {
      const response = await EmployeeService.getEmployeeQualifications(employeeId);
      employeeQualifications.value = response.map(
        (qual) => new EmployeeQualificationListItem(qual)
      );
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : 'Failed to load employee qualifications';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const saveEmployeeQualifications = async (
    employeeId: number,
    qualificationItems: any
  ): Promise<void> => {
    loading.value = true;
    error.value = null;
    try {
      await EmployeeService.saveEmployeeQualifications(employeeId, qualificationItems);
      await loadEmployeeQualifications(employeeId);
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : 'Failed to save employee qualifications';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const loadExpiringQualifications = async (daysThreshold: number = 30): Promise<void> => {
    loading.value = true;
    error.value = null;
    try {
      const response = await EmployeeService.getExpiringQualifications(daysThreshold);
      employeeQualifications.value = response.map(
        (qual) => new EmployeeQualificationListItem(qual)
      );
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : 'Failed to load expiring qualifications';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  // Actions - Work Schedules
  const loadEmployeeSchedules = async (employeeId: number): Promise<void> => {
    loading.value = true;
    error.value = null;
    try {
      const response = await EmployeeService.getEmployeeSchedule(employeeId);
      employeeSchedules.value = response.map((schedule) => new WorkScheduleListItem(schedule));
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : 'Failed to load employee schedules';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const createSchedule = async (request: WorkScheduleRequest): Promise<void> => {
    loading.value = true;
    error.value = null;
    try {
      const response = await EmployeeService.createSchedule(request);
      const newSchedule = new WorkScheduleListItem(response);
      employeeSchedules.value.push(newSchedule);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create schedule';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const updateSchedule = async (
    scheduleId: number,
    request: WorkScheduleRequest
  ): Promise<void> => {
    loading.value = true;
    error.value = null;
    try {
      const response = await EmployeeService.updateSchedule(scheduleId, request);
      const updatedSchedule = new WorkScheduleListItem(response);

      const index = employeeSchedules.value.findIndex(
        (schedule) => schedule.scheduleId === scheduleId
      );
      if (index !== -1) {
        employeeSchedules.value[index] = updatedSchedule;
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update schedule';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const deleteSchedule = async (scheduleId: number): Promise<void> => {
    loading.value = true;
    error.value = null;
    try {
      await EmployeeService.deleteSchedule(scheduleId);
      employeeSchedules.value = employeeSchedules.value.filter(
        (schedule) => schedule.scheduleId !== scheduleId
      );
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete schedule';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  // Actions - Unavailability
  const loadEmployeeUnavailabilities = async (employeeId: number): Promise<void> => {
    loading.value = true;
    error.value = null;
    try {
      const response = await EmployeeService.getEmployeeUnavailability(employeeId);
      employeeUnavailabilities.value = response.map(
        (unavailability) => new EmployeeUnavailabilityListItem(unavailability)
      );
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : 'Failed to load employee unavailabilities';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const createUnavailability = async (
    request: EmployeeUnavailabilityRequest
  ): Promise<void> => {
    loading.value = true;
    error.value = null;
    try {
      const response = await EmployeeService.createUnavailability(request);
      const newUnavailability = new EmployeeUnavailabilityListItem(response);
      employeeUnavailabilities.value.push(newUnavailability);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create unavailability';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const deleteUnavailability = async (unavailabilityId: number): Promise<void> => {
    loading.value = true;
    error.value = null;
    try {
      await EmployeeService.deleteUnavailability(unavailabilityId);
      employeeUnavailabilities.value = employeeUnavailabilities.value.filter(
        (unavailability) => unavailability.unavailabilityId !== unavailabilityId
      );
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete unavailability';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const loadAbsencesByEmployeeIdAndDateRange = async (
    employeeId: number,
    startDate?: Date,
    endDate?: Date
  ): Promise<void> => {
    loading.value = true;
    error.value = null;
    try {
      const response = await EmployeeService.getAbsencesByEmployeeIdAndDateRange(
        employeeId,
        startDate,
        endDate
      );
      employeeUnavailabilities.value = response.map(
        (absence) => new EmployeeUnavailabilityListItem(absence)
      );
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load absences';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const loadAbsencesForCalendar = async (
    startDate?: Date,
    endDate?: Date
  ): Promise<void> => {
    loading.value = true;
    error.value = null;
    try {
      absencesForCalendar.value = await EmployeeService.getAbsencesForCalendar(
        startDate,
        endDate
      );
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load calendar absences';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  return {
    // State
    employees,
    currentEmployee,
    employeeQualifications,
    employeeSchedules,
    employeeUnavailabilities,
    absencesForCalendar,
    loading,
    error,
    // Getters
    activeEmployees,
    employeesWithExpiringQualifications,
    employeesWithExpiredQualifications,
    // Actions
    loadEmployees,
    loadEmployee,
    createEmployee,
    updateEmployee,
    deleteEmployee,
    loadEmployeeQualifications,
    saveEmployeeQualifications,
    loadExpiringQualifications,
    loadEmployeeSchedules,
    createSchedule,
    updateSchedule,
    deleteSchedule,
    loadEmployeeUnavailabilities,
    createUnavailability,
    deleteUnavailability,
    loadAbsencesByEmployeeIdAndDateRange,
    loadAbsencesForCalendar
  };
});
